import {
  Alert,
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import type {
  CategoryCode,
  ProductForm,
  UpdateProductForm,
} from './productFormTypes';

type Props = {
  form: ProductForm;
  skuExistsError: string | null;
  skuCheckPending: boolean;
  gtinValidation: boolean | null;
  gtinExistsError: string | null;
  gtinCheckPending: boolean;
  isTextile: boolean;
  isElectronics: boolean;
  isIndustrial: boolean;
  validationErrors: Partial<Record<string, string>>;
  categoryOptions: Array<{ code: CategoryCode; name: string }>;
  updateForm: UpdateProductForm;
  changeCategory: (categoryCode: CategoryCode | '') => void;
  existingNotifiedBodyCertificateName: string | null;
  existingNotifiedBodyCertificateSignedUrl: string | null;
  removeExistingNotifiedBodyCertificate: boolean;
  onRemoveExistingNotifiedBodyCertificate: () => void | Promise<void>;
  onNotifiedBodyCertificateFileChange: (
    file: File | null,
  ) => void | Promise<void>;
};

export function AddProductStepIdentity({
  form,
  skuExistsError,
  skuCheckPending,
  gtinValidation,
  gtinExistsError,
  gtinCheckPending,
  isTextile,
  isElectronics,
  isIndustrial,
  validationErrors,
  categoryOptions,
  updateForm,
  changeCategory,
  existingNotifiedBodyCertificateName,
  existingNotifiedBodyCertificateSignedUrl,
  removeExistingNotifiedBodyCertificate,
  onRemoveExistingNotifiedBodyCertificate,
  onNotifiedBodyCertificateFileChange,
}: Props) {
  const sanitizeWeightInput = (value: string) => {
    const digitsOnly = value.replace(/[^0-9.]/g, '');
    const [wholePart = '', ...decimalParts] = digitsOnly.split('.');

    if (decimalParts.length === 0) return digitsOnly;

    return `${wholePart}.${decimalParts.join('')}`;
  };

  const skuError = validationErrors.skuModel ?? skuExistsError;
  const gtinError =
    validationErrors.gtinEan ??
    gtinExistsError ??
    (gtinValidation === false
      ? 'Enter a valid 13 or 14-digit GS1 checksum.'
      : null);
  const validationMessages = Object.values(validationErrors).filter(Boolean);
  const usesAuthorizedRepresentative =
    form.ceResponsibleEntityType === 'AUTHORIZED_REPRESENTATIVE';
  const showExistingNotifiedBodyCertificate = Boolean(
    existingNotifiedBodyCertificateName &&
    !removeExistingNotifiedBodyCertificate,
  );
  const responsiblePersonName = usesAuthorizedRepresentative
    ? form.ceResponsibleCompanyName.trim()
    : form.importedOutsideEu
      ? form.importerName.trim()
      : form.manufacturerName.trim();
  const responsiblePersonAddress = usesAuthorizedRepresentative
    ? form.ceResponsibleEuAddress.trim()
    : form.importedOutsideEu
      ? form.importerAddress.trim()
      : form.manufacturerAddress.trim();

  const shortenFileName = (value: string, maxLength = 28) => {
    if (value.length <= maxLength) return value;

    const extensionIndex = value.lastIndexOf('.');
    const hasExtension =
      extensionIndex > 0 && extensionIndex >= value.length - 8;
    const extension = hasExtension ? value.slice(extensionIndex) : '';
    const baseName = hasExtension ? value.slice(0, extensionIndex) : value;
    const availableBaseLength = Math.max(8, maxLength - extension.length - 1);

    return `${baseName.slice(0, availableBaseLength)}...${extension}`;
  };

  const renderInfoTooltip = (
    tooltipId: string,
    tooltipText: string,
    ariaLabel: string,
  ) => (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip id={tooltipId}>{tooltipText}</Tooltip>}
    >
      <button
        type="button"
        className="btn btn-link text-muted p-0 border-0 lh-1"
        aria-label={ariaLabel}
      >
        <i className="bi bi-info-circle-fill" />
      </button>
    </OverlayTrigger>
  );

  return (
    <Row className="g-3">
      {validationMessages.length > 0 && (
        <Col xs={12}>
          <Alert variant="danger">
            <ul className="mb-0 ps-3">
              {validationMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        </Col>
      )}

      <Col md={8}>
        <Form.Group controlId="productName">
          <Form.Label>Product Name *</Form.Label>
          <Form.Control
            value={form.name}
            isInvalid={Boolean(validationErrors.name)}
            onChange={(event) => updateForm('name', event.target.value)}
            placeholder="Industrial Electric Motor X1"
          />
          <Form.Control.Feedback type="invalid">
            {validationErrors.name}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col md={4}>
        <Form.Group controlId="productSku">
          <Form.Label>SKU / Model Number *</Form.Label>
          <Form.Control
            value={form.skuModel}
            isInvalid={Boolean(skuError)}
            isValid={
              !skuError && !skuCheckPending && Boolean(form.skuModel.trim())
            }
            onChange={(event) => updateForm('skuModel', event.target.value)}
          />
          {!skuError && (
            <Form.Text>
              {skuCheckPending
                ? 'Checking whether this SKU / Model Number already exists...'
                : 'Must be unique within your organisation.'}
            </Form.Text>
          )}
          <Form.Control.Feedback type="invalid">
            {skuError}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col xs={12}>
        <Form.Group controlId="productSummary">
          <div className="d-flex align-items-center justify-content-between gap-3">
            <Form.Label className="mb-1">Product Summary</Form.Label>
            <Form.Text className="text-muted">
              {form.productSummary.length}/300
            </Form.Text>
          </div>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={300}
            value={form.productSummary}
            onChange={(event) =>
              updateForm('productSummary', event.target.value)
            }
            placeholder="Short public summary of the product and its purpose."
          />
          <Form.Text className="text-muted">
            Shown in the public passport and preview.
          </Form.Text>
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group controlId="productWeight">
          <Form.Label>Product Weight</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control
              value={form.productWeight}
              inputMode="decimal"
              onChange={(event) =>
                updateForm(
                  'productWeight',
                  sanitizeWeightInput(event.target.value),
                )
              }
              placeholder="250"
            />
            <Form.Select
              value={form.productWeightUnit}
              onChange={(event) =>
                updateForm(
                  'productWeightUnit',
                  event.target.value as 'g' | 'kg',
                )
              }
              style={{ maxWidth: '120px' }}
            >
              <option value="g">grams</option>
              <option value="kg">kgs</option>
            </Form.Select>
          </div>
          <Form.Text className="text-muted">
            Shown in the public passport and preview.
          </Form.Text>
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group controlId="productGtin">
          <Form.Label>GTIN / EAN Barcode *</Form.Label>
          <Form.Control
            value={form.gtinEan}
            inputMode="numeric"
            maxLength={14}
            isInvalid={Boolean(gtinError)}
            isValid={
              gtinValidation === true && !gtinExistsError && !gtinCheckPending
            }
            onChange={(event) =>
              updateForm('gtinEan', event.target.value.replace(/\D/g, ''))
            }
          />
          <Form.Text className={gtinError ? 'text-danger' : undefined}>
            {gtinError
              ? gtinError
              : gtinCheckPending
                ? 'Checking whether this GTIN already exists...'
                : 'Required for publication.'}
          </Form.Text>
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group controlId="productBrand">
          <Form.Label>Brand Name *</Form.Label>
          <Form.Control
            value={form.brandName}
            isInvalid={Boolean(validationErrors.brandName)}
            onChange={(event) => updateForm('brandName', event.target.value)}
          />
          <Form.Control.Feedback type="invalid">
            {validationErrors.brandName}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group controlId="productCategory">
          <Form.Label>Product Category *</Form.Label>
          <Form.Select
            value={form.categoryCode}
            isInvalid={Boolean(validationErrors.categoryCode)}
            onChange={(event) =>
              changeCategory(event.target.value as CategoryCode | '')
            }
          >
            <option value="">Select an EU ESPR priority group</option>
            {categoryOptions.map((category) => (
              <option key={category.code} value={category.code}>
                {category.name}
              </option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {validationErrors.categoryCode}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      {isTextile && (
        <>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Season / Collection Name</Form.Label>
              <Form.Control
                value={form.seasonCollection}
                onChange={(event) =>
                  updateForm('seasonCollection', event.target.value)
                }
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Label>Target Audience</Form.Label>
            <div className="d-flex flex-wrap gap-3">
              {["Men's", "Women's", 'Unisex', "Children's"].map((audience) => (
                <Form.Check
                  inline
                  key={audience}
                  id={`targetAudience-${audience}`}
                  label={audience}
                  type="checkbox"
                  checked={form.targetAudience.includes(audience)}
                  onChange={() =>
                    updateForm(
                      'targetAudience',
                      form.targetAudience.includes(audience)
                        ? form.targetAudience.filter(
                            (value) => value !== audience,
                          )
                        : [...form.targetAudience, audience],
                    )
                  }
                />
              ))}
            </div>
          </Col>
        </>
      )}
      {isElectronics && (
        <Col md={6}>
          <Form.Group>
            <Form.Label>Hardware Revision / Batch Number</Form.Label>
            <Form.Control
              value={form.hardwareRevision}
              onChange={(event) =>
                updateForm('hardwareRevision', event.target.value)
              }
            />
          </Form.Group>
        </Col>
      )}
      {isIndustrial && (
        <>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Machine Type / CE Classification *</Form.Label>
              <Form.Select
                value={form.machineClassification}
                isInvalid={Boolean(validationErrors.machineClassification)}
                onChange={(event) =>
                  updateForm(
                    'machineClassification',
                    event.target.value as ProductForm['machineClassification'],
                  )
                }
              >
                <option value="">Select CE classification</option>
                <option value="STANDARD_MACHINERY">
                  Standard Machinery - Self-Declaration
                </option>
                <option value="HIGH_RISK_MACHINERY">
                  High-Risk Machinery - Annex I Notified Body Required
                </option>
                <option value="PARTLY_COMPLETED_MACHINERY">
                  Partly Completed Machinery - Declaration of Incorporation
                </option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {validationErrors.machineClassification}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          {form.machineClassification === 'HIGH_RISK_MACHINERY' && (
            <>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Notified Body Number *</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.notifiedBodyNumber}
                    inputMode="numeric"
                    maxLength={4}
                    pattern="^\\d{4}$"
                    isInvalid={Boolean(validationErrors.notifiedBodyNumber)}
                    onChange={(event) =>
                      updateForm(
                        'notifiedBodyNumber',
                        event.target.value.replace(/\D/g, '').slice(0, 4),
                      )
                    }
                    placeholder="0035"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.notifiedBodyNumber}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Notified Body Cert Number *</Form.Label>
                  <Form.Control
                    value={form.notifiedBodyCertNumber}
                    isInvalid={Boolean(validationErrors.notifiedBodyCertNumber)}
                    onChange={(event) =>
                      updateForm('notifiedBodyCertNumber', event.target.value)
                    }
                    placeholder="CE-DE-2026-8891"
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.notifiedBodyCertNumber}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Notified Body Certificate *</Form.Label>
                  {showExistingNotifiedBodyCertificate ? (
                    <div className="small">
                      <div className="text-muted mb-1">
                        Current uploaded file
                      </div>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        {existingNotifiedBodyCertificateSignedUrl ? (
                          <a
                            href={existingNotifiedBodyCertificateSignedUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {shortenFileName(
                              existingNotifiedBodyCertificateName ??
                                'Uploaded file',
                            )}
                          </a>
                        ) : (
                          <span>
                            {shortenFileName(
                              existingNotifiedBodyCertificateName ??
                                'Uploaded file',
                            )}
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="link text-danger p-0"
                          onClick={() =>
                            void onRemoveExistingNotifiedBodyCertificate()
                          }
                        >
                          <i className="bi bi-x-circle-fill"></i>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Form.Control
                        type="file"
                        accept=".pdf,application/pdf"
                        isInvalid={Boolean(
                          validationErrors.notifiedBodyCertificateFile,
                        )}
                        onChange={(event) =>
                          void onNotifiedBodyCertificateFileChange(
                            (event.target as HTMLInputElement).files?.[0] ??
                              null,
                          )
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.notifiedBodyCertificateFile}
                      </Form.Control.Feedback>
                      {form.notifiedBodyCertificateFile && (
                        <div className="small mt-2 d-flex flex-wrap align-items-center gap-2">
                          <span className="text-muted">
                            Selected:{' '}
                            {shortenFileName(
                              form.notifiedBodyCertificateFile.name,
                            )}
                          </span>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() =>
                              void onNotifiedBodyCertificateFileChange(null)
                            }
                          >
                            Clear
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </Form.Group>
              </Col>
            </>
          )}
        </>
      )}

      <Col xs={12}>
        <div className="border rounded bg-light p-3">
          <div className="fw-semibold mb-3">Economic Operator Details</div>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Manufacturer Legal Name *</Form.Label>
                <Form.Control
                  value={form.manufacturerName}
                  isInvalid={Boolean(validationErrors.manufacturerName)}
                  onChange={(event) =>
                    updateForm('manufacturerName', event.target.value)
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.manufacturerName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Manufacturer Address *</Form.Label>
                <Form.Control
                  placeholder="Complete physical address"
                  value={form.manufacturerAddress}
                  isInvalid={Boolean(validationErrors.manufacturerAddress)}
                  onChange={(event) =>
                    updateForm('manufacturerAddress', event.target.value)
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.manufacturerAddress}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Check
                type="switch"
                id="importedOutsideEu"
                label="Imported from outside the EU"
                checked={form.importedOutsideEu}
                onChange={(event) => {
                  const nextImportedOutsideEu = event.target.checked;
                  updateForm('importedOutsideEu', nextImportedOutsideEu);

                  if (nextImportedOutsideEu) {
                    if (!usesAuthorizedRepresentative) {
                      updateForm('ceResponsibleEntityType', 'EU_IMPORTER');
                    }
                    return;
                  }

                  if (
                    form.ceResponsibleEntityType !== 'AUTHORIZED_REPRESENTATIVE'
                  ) {
                    updateForm('ceResponsibleEntityType', '');
                  }
                }}
              />
            </Col>
            {form.importedOutsideEu && (
              <>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>EU Importer Name *</Form.Label>
                    <Form.Control
                      value={form.importerName}
                      isInvalid={Boolean(validationErrors.importerName)}
                      onChange={(event) =>
                        updateForm('importerName', event.target.value)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.importerName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>Importer Email *</Form.Label>
                    <Form.Control
                      type="email"
                      value={form.importerEmail}
                      isInvalid={Boolean(validationErrors.importerEmail)}
                      onChange={(event) =>
                        updateForm('importerEmail', event.target.value)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.importerEmail}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>EU Importer Address *</Form.Label>
                    <Form.Control
                      placeholder="Complete physical address"
                      value={form.importerAddress}
                      isInvalid={Boolean(validationErrors.importerAddress)}
                      onChange={(event) =>
                        updateForm('importerAddress', event.target.value)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.importerAddress}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label>EORI Number *</Form.Label>
                    <Form.Control
                      value={form.importerEori}
                      isInvalid={Boolean(validationErrors.importerEori)}
                      onChange={(event) =>
                        updateForm('importerEori', event.target.value)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.importerEori}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </>
            )}
            {isIndustrial && (
              <>
                <Col xs={12}>
                  <Form.Check
                    type="switch"
                    id="useAuthorizedRepresentative"
                    label="Designate an Authorized Representative (AR) in the EU"
                    checked={usesAuthorizedRepresentative}
                    onChange={(event) =>
                      updateForm(
                        'ceResponsibleEntityType',
                        event.target.checked
                          ? 'AUTHORIZED_REPRESENTATIVE'
                          : form.importedOutsideEu
                            ? 'EU_IMPORTER'
                            : '',
                      )
                    }
                  />
                </Col>
                {usesAuthorizedRepresentative && (
                  <>
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>AR Company Name *</Form.Label>
                        <Form.Control
                          value={form.ceResponsibleCompanyName}
                          isInvalid={Boolean(
                            validationErrors.ceResponsibleCompanyName,
                          )}
                          onChange={(event) =>
                            updateForm(
                              'ceResponsibleCompanyName',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.ceResponsibleCompanyName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>AR Address *</Form.Label>
                        <Form.Control
                          placeholder="Complete physical address"
                          value={form.ceResponsibleEuAddress}
                          isInvalid={Boolean(
                            validationErrors.ceResponsibleEuAddress,
                          )}
                          onChange={(event) =>
                            updateForm(
                              'ceResponsibleEuAddress',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.ceResponsibleEuAddress}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>AR Email *</Form.Label>
                        <Form.Control
                          type="email"
                          value={form.ceResponsibleContactEmail}
                          isInvalid={Boolean(
                            validationErrors.ceResponsibleContactEmail,
                          )}
                          onChange={(event) =>
                            updateForm(
                              'ceResponsibleContactEmail',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.ceResponsibleContactEmail}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </>
                )}
                <Col xs={12}>
                  <div className="small text-muted pt-2 fw-semibold border-top">
                    <span className="d-inline-flex align-items-center gap-1">
                      {renderInfoTooltip(
                        'ce-responsible-person-tooltip',
                        'The CE Responsible Person is the EU-established entity (EU Manufacturer, Importer, or Authorized Representative) legally required to hold technical documentation and act as the primary contact for EU market surveillance authorities under Regulation (EU) 2019/1020.',
                        'About the CE Responsible Person',
                      )}
                      <span>
                        CE Responsible Person (EU 2019/1020):{' '}
                        {responsiblePersonName || 'Company name required'},{' '}
                        {responsiblePersonAddress || 'Address required'}
                      </span>
                    </span>
                  </div>
                </Col>
              </>
            )}
          </Row>
        </div>
      </Col>
    </Row>
  );
}
